import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const ENTITY_TYPES = [
  { v: "purchase_requisition", l: "Purchase Requisition" },
  { v: "purchase_order", l: "Purchase Order" },
  { v: "vendor_invoice", l: "Vendor Invoice" },
  { v: "grn", l: "GRN" },
];
const ROLES = ["PROCUREMENT_MANAGER", "ORG_ADMIN", "FINANCE_MANAGER", "INVENTORY_MANAGER"];

export default function ApprovalRules() {
  const [rules, setRules] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", entity_type: "purchase_order", min_value: 0, max_value: 0, levels: [{ level: 1, approver_role: "PROCUREMENT_MANAGER" }] });

  const load = async () => { const { data } = await api.get("/approvals/rules"); setRules(data); };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/approvals/rules", { ...form, min_value: Number(form.min_value), max_value: Number(form.max_value) });
      toast.success("Rule created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this rule?")) return;
    await api.delete(`/approvals/rules/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="approval-rules-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Approval matrix</h1>
          <p className="text-muted-foreground">Configure who approves what, by value band.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-rule-btn"><Plus className="w-4 h-4 mr-1" /> New rule</Button></DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid="rule-dialog">
            <DialogHeader><DialogTitle>New approval rule</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rule-name-input" /></div>
              <div className="space-y-2"><Label>Entity type</Label>
                <Select value={form.entity_type} onValueChange={(v) => setForm({ ...form, entity_type: v })}>
                  <SelectTrigger data-testid="rule-entity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ENTITY_TYPES.map((e) => <SelectItem key={e.v} value={e.v}>{e.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Min value (₹)</Label><Input type="number" value={form.min_value} onChange={(e) => setForm({ ...form, min_value: e.target.value })} data-testid="rule-min-input" /></div>
                <div className="space-y-2"><Label>Max value (₹, 0 = ∞)</Label><Input type="number" value={form.max_value} onChange={(e) => setForm({ ...form, max_value: e.target.value })} data-testid="rule-max-input" /></div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Approval levels (sequential)</Label>
                  <Button size="sm" variant="outline" onClick={() => setForm({ ...form, levels: [...form.levels, { level: form.levels.length + 1, approver_role: "ORG_ADMIN" }] })} data-testid="rule-add-level-btn">+ Add level</Button>
                </div>
                {form.levels.map((lvl, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="font-mono text-xs w-8">L{lvl.level}</span>
                    <Select value={lvl.approver_role} onValueChange={(v) => { const ls = [...form.levels]; ls[idx] = { ...ls[idx], approver_role: v }; setForm({ ...form, levels: ls }); }}>
                      <SelectTrigger className="flex-1" data-testid={`rule-level-${idx}-role`}><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                    {form.levels.length > 1 && (
                      <button onClick={() => setForm({ ...form, levels: form.levels.filter((_, i) => i !== idx).map((l, i) => ({ ...l, level: i + 1 })) })} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <Button onClick={submit} data-testid="rule-save-btn">Save rule</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Entity</TableHead><TableHead>Min</TableHead><TableHead>Max</TableHead><TableHead>Levels</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rules.map((r) => (
              <TableRow key={r.id} data-testid={`rule-row-${r.id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{r.entity_type}</Badge></TableCell>
                <TableCell className="font-mono">₹{Number(r.min_value).toLocaleString()}</TableCell>
                <TableCell className="font-mono">{r.max_value ? `₹${Number(r.max_value).toLocaleString()}` : "∞"}</TableCell>
                <TableCell className="font-mono text-xs">{r.levels.map((l) => `L${l.level}:${l.approver_role}`).join(" → ")}</TableCell>
                <TableCell><button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button></TableCell>
              </TableRow>
            ))}
            {rules.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No rules. Submissions will auto-approve until you add a rule.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
